import { useCallback, useEffect, useState } from "react";

// Define a minimal interface for the store
interface Store {
	has: (key: string) => boolean;
	get: <T>(key: string) => T;
	set: <T>(key: string, value: T) => void;
}

/**
 * A hook that provides state that is:
 * 1. Persisted to localStorage
 * 2. Synced between multiple components/instances that use the same key
 */
export function useSyncedPersistentState<T>(
	store: Store,
	key: string,
	initialValue: T,
	initalValueOverride = false,
): [T, (value: T | ((prev: T) => T)) => void] {
	// Get initial value from store or initialValue param
	const [state, setState] = useState<T>(() => {
		if (initalValueOverride) {
			return initialValue;
		}
		return store.has(key) ? store.get(key) : initialValue;
	});

	// Handle updates from other components via localStorage and custom events
	useEffect(() => {
		// The storage event handler function (works across tabs/windows)
		const handleStorageChange = (event: StorageEvent) => {
			// If the initial value was overridden, don't update the state from events
			if (initalValueOverride) return;

			// Check if the change is relevant to our key
			// The key in the event includes namespace, so we need to check if it ends with our key
			if (event.key && event.key.endsWith(key)) {
				try {
					// Parse the new value from localStorage
					const newValue = JSON.parse(event.newValue || "null");
					// Update state if changed
					setState(newValue);
				} catch (e) {
					console.error("Error parsing localStorage value:", e);
				}
			}
		};

		// Extract the base key without suffixes (for filters that should be shared across all views)
		const baseKey = key.split("-")[0];

		// Custom event handler for same-window updates
		const handleCustomEvent = (event: CustomEvent<{ key: string; value: T }>) => {
			// If the initial value was overridden, don't update the state from events
			if (initalValueOverride) return;
			// Check if the event key matches our key exactly
			// For filter toggles, we also check if the base key matches
			// This ensures filter changes sync across all views
			if (event.detail.key === key || (baseKey && event.detail.key.split("-")[0] === baseKey && !key.includes("-"))) {
				setState(event.detail.value);
			}
		};

		// Subscribe to storage events (across windows)
		window.addEventListener("storage", handleStorageChange);

		// Subscribe to custom events (within same window)
		window.addEventListener("localStorageSync", handleCustomEvent as EventListener);

		// Cleanup on unmount
		return () => {
			window.removeEventListener("storage", handleStorageChange);
			window.removeEventListener("localStorageSync", handleCustomEvent as EventListener);
		};
	}, [key, initalValueOverride]);

	// Update function that both sets state and writes to localStorage
	const setStateAndStore = useCallback(
		(value: T | ((prev: T) => T)) => {
			setState(prevState => {
				// Handle functional updates
				const newValue = typeof value === "function" ? (value as (prev: T) => T)(prevState) : value;

				// Persist to localStorage
				store.set(key, newValue);

				// Dispatch a custom event to notify other components in the same window
				const event = new CustomEvent("localStorageSync", {
					detail: { key, value: newValue },
				});
				window.dispatchEvent(event);

				return newValue;
			});
		},
		[key, store],
	);

	return [state, setStateAndStore];
}

export function useSyncedPersistentStateWithOverride<T>(
	store: Store,
	key: string,
	initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
	return useSyncedPersistentState(store, key, initialValue, true);
}
